/**
 * What the event editor (LandingTab) needs from a template definition, for both
 * kinds of template (JUB-161).
 *
 * `GET /templates` returns the raw stored config: a legacy `EventTemplateConfig`
 * for the built-in designs, the whole package for an installed template package.
 * The editor's template cards and its preview chrome used to read legacy fields
 * (`config.preview`, `config.surfaces`, `config.hero`) straight off whichever one
 * arrived, which a package does not have. These accessors never read a raw
 * package field and never throw: a package goes through `paletteConfigFor`, a
 * legacy config is read field by field.
 */

import { FALLBACK_TEMPLATES, isSelectableTemplate, type EventTemplateDefinition } from "@/lib/event-templates";
import { isTemplatePackage, paletteConfigFor } from "./resolve";

/**
 * May a customer pick this template? A current built-in design, or an installed
 * package. `GET /templates` only returns active rows, so a package listed there
 * is active; a definition that says it is inactive (`active: false`, the shape
 * of the management endpoints) is never offered. Retired designs keep their own
 * rule (`isSelectableTemplate`).
 */
export function isOfferedTemplate(template: EventTemplateDefinition): boolean {
  if (template.active === false) return false;
  return isSelectableTemplate(template.key) || isTemplatePackage(template.config);
}

/**
 * What a design chooser offers: the catalog `GET /templates` listed, or the
 * built-in designs while it has not answered or when it failed, narrowed by
 * `isOfferedTemplate`. Used by the onboarding wizard's design choice (JUB-213).
 */
export function offeredTemplatesOf(catalog: EventTemplateDefinition[] | null | undefined): EventTemplateDefinition[] {
  return (catalog && catalog.length > 0 ? catalog : FALLBACK_TEMPLATES).filter(isOfferedTemplate);
}

/** An installed package's own name, from its manifest; null for a built-in design. */
export function packageNameOf(template: EventTemplateDefinition): string | null {
  if (isSelectableTemplate(template.key)) return null;
  const config: unknown = template.config;
  return isTemplatePackage(config) ? config.manifest.name || template.name : null;
}

export type TemplateCardPreview = { background?: string; emoji: string; tagline: string };

/** The swatch, emoji and tagline of a template card in the chooser. */
export function templateCardPreviewOf(template: EventTemplateDefinition): TemplateCardPreview {
  const config: unknown = template.config;
  if (isTemplatePackage(config)) {
    const surfaces = paletteConfigFor(config);
    return {
      background: surfaces.surfaces.cardBackground ?? surfaces.palette.background,
      emoji: "✨",
      tagline: config.manifest.description ?? template.description ?? "",
    };
  }
  const legacy = template.config as Partial<EventTemplateDefinition["config"]> | undefined;
  return {
    background: legacy?.preview?.background ?? legacy?.palette?.background,
    emoji: legacy?.preview?.emoji ?? "✨",
    tagline: legacy?.preview?.tagline ?? template.description ?? "",
  };
}

/** Colours and fonts of the editor's preview card around the live preview. */
export type EditorChrome = {
  hero: { background?: string; textColor?: string; buttonBg?: string; buttonText?: string; buttonBorder?: string };
  surfaces: { cardBackground?: string; cardBorder?: string };
  typography: { heading?: string };
};

/**
 * For a package: hero background and text from `palette.background` and
 * `palette.text` (the package's own page colours), buttons, card surfaces and
 * heading font from the host-surface bridge.
 */
export function editorChromeOf(template: EventTemplateDefinition): EditorChrome {
  const config: unknown = template.config;
  if (isTemplatePackage(config)) {
    const bridged = paletteConfigFor(config);
    return {
      hero: {
        background: bridged.palette.background,
        textColor: bridged.palette.text,
        buttonBg: bridged.hero.buttonBg,
        buttonText: bridged.hero.buttonText,
      },
      surfaces: { cardBackground: bridged.surfaces.cardBackground, cardBorder: bridged.surfaces.cardBorder },
      typography: { heading: bridged.typography.heading },
    };
  }
  const legacy = template.config as Partial<EventTemplateDefinition["config"]> | undefined;
  return {
    hero: { ...(legacy?.hero ?? {}) },
    surfaces: { cardBackground: legacy?.surfaces?.cardBackground, cardBorder: legacy?.surfaces?.cardBorder },
    typography: { heading: legacy?.typography?.heading },
  };
}
